import { supabase } from '../lib/supabase';

/**
 * Font Service - Smart Font Weight Loading & Fallback System
 * 
 * Loads only selected fonts from Supabase database with intelligent weight fallback.
 * Supports per-generation font loading without cross-generation caching.
 * 
 * Database Schema:
 * - fonts table columns: id, font_name, font_weight, base64_data, file_name, content_usage, font_description
 */

class FontService {
  constructor() {
    this.fontCache = new Map();
    this.loadedFonts = new Set();
    this.currentSelection = null; // Track current font selection
    this.maxRetries = 3;
    this.retryDelay = 1000;
  }

  /**
   * Weight fallback hierarchy - tries bold first, then normal
   * This ensures fonts always render even if requested weight is unavailable
   */
  weightFallbackMap = {
    'bold': ['bold', 'normal'], // Try bold first, fallback to normal
    'italic': ['italic', 'normal'], // Try italic first, fallback to normal  
    'normal': ['normal'], // Always available (database constraint)
    'bolditalic': ['bolditalic', 'bold', 'italic', 'normal'] // Future support
  };

  /**
   * Check Supabase connection health
   * @returns {Promise<boolean>} True if connection is healthy
   */
  async checkConnection() {
    try {
      if (!supabase) {
        console.error('❌ Supabase client not initialized');
        return false;
      }

      // Simple query to test connection
      const { error } = await supabase?.from('fonts')?.select('id')?.limit(1);
      
      if (error) {
        console.error('❌ Supabase connection test failed:', error?.message);
        return false;
      }

      console.log('✅ Supabase connection healthy');
      return true;
    } catch (error) {
      console.error('❌ Connection check failed:', error?.message);
      return false;
    }
  }

  /**
   * Retry function with exponential backoff
   * @param {Function} operation - Async operation to retry
   * @param {number} retries - Number of retry attempts
   * @returns {Promise<any>} Operation result
   */
  async retryOperation(operation, retries = this.maxRetries) {
    let lastError;
    
    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        console.log(`🔄 Attempt ${attempt}/${retries}...`);
        return await operation();
      } catch (error) {
        lastError = error;
        console.warn(`⚠️ Attempt ${attempt} failed:`, error?.message);
        
        if (attempt < retries) {
          const delay = this.retryDelay * Math.pow(2, attempt - 1); // Exponential backoff
          console.log(`⏳ Waiting ${delay}ms before retry...`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }
    
    throw lastError;
  }

  /**
   * Load ONLY selected fonts from database (selective loading)
   * @param {string} contentFont - Font name for body content
   * @param {string} titleFont - Font name for titles/headers
   * @returns {Promise<Object>} Font data organized by font_name → font_weight → { base64, contentUsage }
   */
  async loadSelectedFonts(contentFont, titleFont) {
    try {
      // Build list of unique font names to load
      const selectedFonts = [contentFont, titleFont]?.filter(Boolean);
      const uniqueFonts = [...new Set(selectedFonts)]; // Remove duplicates

      if (uniqueFonts?.length === 0) {
        console.warn('⚠️ No fonts selected for loading');
        return {};
      }

      console.log(`📚 Loading selected fonts from database: ${uniqueFonts?.join(', ')}`);

      // Check connection before attempting to load
      const isConnected = await this.checkConnection();
      if (!isConnected) {
        console.error('❌ Cannot load fonts: Supabase connection unavailable');
        console.log('💡 Falling back to browser default fonts');
        return this.getFallbackFonts(uniqueFonts);
      }

      // Wrap database query in retry logic
      const loadOperation = async () => {
        const { data: fonts, error } = await supabase
          ?.from('fonts')
          ?.select('font_name, font_weight, base64_data, file_name, content_usage, font_description, display_name')
          ?.in('font_name', uniqueFonts);

        if (error) {
          console.error('❌ Database query error:', {
            message: error?.message,
            details: error?.details,
            hint: error?.hint,
            code: error?.code
          });
          throw error;
        }

        return fonts;
      };

      const fonts = await this.retryOperation(loadOperation);

      if (!fonts || fonts?.length === 0) {
        console.warn(`⚠️ No fonts found in database for: ${uniqueFonts?.join(', ')}`);
        console.log('💡 Using fallback fonts');
        return this.getFallbackFonts(uniqueFonts);
      }

      // Organize fonts by name and weight with availability tracking
      const fontMap = {};
      fonts?.forEach((font) => {
        const { font_name, font_weight, base64_data, content_usage, font_description, display_name, size_factor } = font;

        if (!fontMap?.[font_name]) {
          fontMap[font_name] = {
            available: [], // Track available weights
            base64Data: {},
            contentUsage: content_usage,
            description: font_description,
            displayName: display_name || font_name?.charAt(0)?.toUpperCase() + font_name?.slice(1),
            sizeFactor: size_factor
          };
        }

        // Store weight data and track availability
        fontMap[font_name].base64Data[font_weight] = base64_data;
        fontMap?.[font_name]?.available?.push(font_weight);
      });

      // Log availability summary
      Object.entries(fontMap)?.forEach(([fontName, fontData]) => {
        console.log(`✅ ${fontName}: ${fontData?.available?.join(', ')} weights available`);
      });

      // Store in cache with selection metadata
      this.fontCache?.set('selectedFonts', fontMap);
      this.currentSelection = { contentFont, titleFont };

      console.log(`✅ Loaded ${fonts?.length} font variants for ${uniqueFonts?.length} fonts`);
      return fontMap;

    } catch (error) {
      console.error('❌ Fatal error loading selected fonts:', {
        message: error?.message,
        stack: error?.stack
      });
      console.log('💡 Falling back to browser default fonts');
      return this.getFallbackFonts([contentFont, titleFont]?.filter(Boolean));
    }
  }

  /**
   * Get fallback fonts when database is unavailable
   * @param {Array<string>} requestedFonts - Font names that were requested
   * @returns {Object} Fallback font configuration
   */
  getFallbackFonts(requestedFonts) {
    console.log('🔄 Creating fallback font configuration');
    
    const fallbackMap = {};
    const browserFonts = ['Arial', 'Helvetica', 'Times New Roman', 'Georgia', 'Verdana'];
    
    requestedFonts?.forEach((fontName, index) => {
      const fallbackFont = browserFonts?.[index % browserFonts?.length] || 'Arial';
      
      fallbackMap[fontName] = {
        available: ['normal', 'bold'],
        base64Data: {}, // Empty - will use browser fonts
        contentUsage: true,
        description: `Fallback to ${fallbackFont}`,
        isFallback: true,
        sizeFactor: 1
      };
      
      console.log(`📝 ${fontName} → ${fallbackFont} (browser font)`);
    });
    
    return fallbackMap;
  }

  /**
   * Get optimal font weight with intelligent fallback
   * If requested weight unavailable, falls back using hierarchy
   * @param {string} fontName - Font name
   * @param {string} requestedWeight - Desired weight (bold, normal, italic)
   * @param {Object} fontMap - Current font availability map
   * @returns {Object} { fontName, weight, base64 } - Actual font to use
   */
  getOptimalWeight(fontName, requestedWeight, fontMap) {
    const fontData = fontMap?.[fontName];
    
    if (!fontData) {
      console.warn(`⚠️ Font not found: ${fontName}, using default`);
      return { fontName: 'Roboto', weight: 'normal', base64: null };
    }

    const availableWeights = fontData?.available || ['normal'];
    const fallbackOptions = this.weightFallbackMap?.[requestedWeight] || ['normal'];

    // Find first available weight from fallback hierarchy
    const actualWeight = fallbackOptions?.find(weight => availableWeights?.includes(weight)) || 'normal';
    /*
    // Log fallback if weight changed
    if (actualWeight !== requestedWeight) {
      console.log(`🔄 Font fallback: ${fontName} ${requestedWeight} → ${actualWeight} (${requestedWeight} not available)`);
    }
    */
    return {
      fontName,
      weight: actualWeight,
      base64: fontData?.base64Data?.[actualWeight]
    };
  }

  /**
   * Get available weights for a specific font
   * @param {string} fontName - Font name to check
   * @returns {Array<string>} List of available weights
   */
  getAvailableWeights(fontName) {
    const fontMap = this.fontCache?.get('selectedFonts') || {};
    return fontMap?.[fontName]?.available || [];
  }

  /**
   * Register selected fonts with jsPDF instance using smart weight fallback
   * @param {jsPDF} pdf - jsPDF instance
   * @param {string} contentFont - Content font name
   * @param {string} titleFont - Title font name
   * @returns {Promise<boolean>} True if fonts registered successfully
   */
  async registerSelectedFonts(pdf, contentFont, titleFont) {
    try {
      // Check if selection changed - if so, reload fonts
      const selectionChanged = !this.currentSelection || 
                              this.currentSelection?.contentFont !== contentFont ||
                              this.currentSelection?.titleFont !== titleFont;

      if (selectionChanged || !this.fontCache?.has('selectedFonts')) {
        console.log('🔄 Font selection changed or cache empty, reloading fonts');
        await this.loadSelectedFonts(contentFont, titleFont);
      } else {
        console.log('✓ Using cached font data (selection unchanged)');
      }

      const fontMap = this.fontCache?.get('selectedFonts') || {};

      if (Object.keys(fontMap)?.length === 0) {
        console.warn('⚠️ No fonts available for registration');
        return false;
      }

      let registeredCount = 0;

      // Register each selected font with all available weights
      Object.entries(fontMap)?.forEach(([fontName, fontInfo]) => {
        // Skip fallback fonts (they don't have base64 data)
        if (fontInfo?.isFallback) {
          console.log(`⏭️ Skipping fallback font: ${fontName}`);
          return;
        }

        fontInfo?.available?.forEach(weight => {
          const fileName = `${fontName}-${weight}.ttf`;
          const base64Data = fontInfo?.base64Data?.[weight];

          if (!base64Data) {
            console.warn(`⚠️ No base64 data for ${fontName} (${weight})`);
            return;
          }

          if (!this.loadedFonts?.has(fileName)) {
            try {
              pdf?.addFileToVFS(fileName, base64Data);
              pdf?.addFont(fileName, fontName, weight);
              this.loadedFonts?.add(fileName);
              registeredCount++;
            } catch (fontError) {
              console.warn(`⚠️ Failed to register ${fontName} (${weight}):`, fontError?.message);
            }
          }
        });
      });

      console.log(`✅ Registered ${registeredCount} font variants with jsPDF`);
      return registeredCount > 0;

    } catch (error) {
      console.error('❌ Error registering fonts with jsPDF:', error);
      return false;
    }
  }

  /**
   * Get font with weight fallback for PDF generation
   * Use this when setting fonts in PDF to ensure requested weight is available
   * @param {string} fontName - Font name
   * @param {string} requestedWeight - Desired weight
   * @returns {Object} { fontName, weight } - Actual font to use
   */
  getFontWithFallback(fontName, requestedWeight = 'normal') {
    const fontMap = this.fontCache?.get('selectedFonts') || {};
    const fontInfo = this.getOptimalWeight(fontName, requestedWeight, fontMap);
    
    return {
      fontName: fontInfo?.fontName,
      weight: fontInfo?.weight
    };
  }

  /**
   * Clear font cache (call before each PDF generation to force reload)
   * This ensures fresh font loading per generation without cross-contamination
   */
  clearCache() {
    console.log('🔄 Clearing font cache for fresh load');
    this.fontCache?.clear();
    this.loadedFonts?.clear();
    this.currentSelection = null;
  }

  /**
   * Get all available fonts from database for wizard selection
   * @returns {Promise<Array>} Array of font metadata objects
   */
  async getAllFontsForWizard() {
    try {
      const { data: fonts, error } = await supabase
        ?.from('fonts')
        ?.select('font_name, content_usage, font_description, size_factor')
        ?.order('font_name');

      if (error) {
        console.error('❌ Error loading fonts for wizard:', error);
        return [];
      }

      // Group by font name to get unique fonts
      const uniqueFonts = {};
      fonts?.forEach(font => {
        if (!uniqueFonts?.[font?.font_name]) {
          uniqueFonts[font?.font_name] = {
            name: font?.font_name,
            contentUsage: font?.content_usage,
            description: font?.font_description || 'No description available',
            sizeFactor: font?.size_factor || 1
          };
        }
      });
      console.log(uniqueFonts);
      return Object.values(uniqueFonts);
      
    } catch (error) {
      console.error('❌ Error fetching fonts for wizard:', error);
      return [];
    }
  }

  /**
   * Get fonts available for content use (content_usage = true)
   * @returns {Promise<Array<string>>} List of font names suitable for content
   */
  async getContentFonts() {
    const fonts = await this.getFonts();
    console.log('Loaded Content fonts: ', fonts);
    const contentFonts = [];

    Object.entries(fonts)?.forEach(([fontName, variants]) => {
      // Check if any variant has content_usage = true
      const hasContentUsage = Object.values(variants)?.some(
        (fontData) => fontData?.contentUsage === true
      );

      if (hasContentUsage) {
        contentFonts?.push(fontName);
        console.log('Found' + fontName);
      }
    });
    console.log('content fonts: ' + contentFonts);
    return contentFonts;
  }

  /**
   * Get fonts available for title/header use only (content_usage = false)
   * @returns {Promise<Array<string>>} List of font names suitable for titles
   */
  async getTitleFonts() {
    const fonts = await this.getFonts();
    const titleFonts = [];

    Object.entries(fonts)?.forEach(([fontName, variants]) => {
      // Check if any variant has content_usage = false
      const hasTitleUsage = Object.values(variants)?.some(
        (fontData) => fontData?.contentUsage === false
      );

      if (hasTitleUsage) {
        titleFonts?.push(fontName);
      }
    });

    return titleFonts;
  }

  /**
   * Get font metadata (name, weights, usage)
   * @returns {Promise<Array>} Array of font metadata objects
   */
  async getFontMetadata() {
    const fonts = await this.getFonts();
    const metadata = [];

    Object.entries(fonts)?.forEach(([fontName, variants]) => {
      const weights = Object.keys(variants);
      const contentUsage = Object.values(variants)?.some((v) => v?.contentUsage);

      metadata?.push({
        name: fontName,
        weights,
        contentUsage,
        isContentFont: contentUsage,
        isTitleFont: !contentUsage
      });
    });

    return metadata;
  }

  /**
   * Get fonts (from cache or database)
   * @returns {Promise<Object>} Font data organized by font_name -> font_weight -> { base64, contentUsage }
   */
  async getFonts() {
    // Return cached fonts if available
   /*
    if (this.fontCache?.has('allFonts')) {
      console.log('✓ Using cached fonts');
      return this.fontCache?.get('allFonts');
    }
    */

    // Load from database if not cached
    return await this.loadFontsFromDatabase();
  }

  /**
   * Load all fonts from Supabase database
   * Organizes fonts by name and weight for easy access
   * @returns {Promise<Object>} Font data organized by font_name -> font_weight -> base64_data
   */
  async loadFontsFromDatabase() {
    try {
      console.log('📚 Loading fonts from Supabase database...');

      const { data: fonts, error } = await supabase?.from('fonts')?.select('font_name, font_weight, base64_data, file_name, content_usage, display_name, font_description, size_factor');

      if (error) {
        console.error('❌ Error loading fonts from database:', error);
        throw error;
      }

      if (!fonts || fonts?.length === 0) {
        console.warn('⚠️ No fonts found in database');
        return {};
      }

      // Organize fonts by name and weight
      const fontMap = {};
      fonts?.forEach((font) => {
        const { font_name, font_weight, base64_data, content_usage, display_name, font_description, size_factor } = font;

        if (!fontMap?.[font_name]) {
          fontMap[font_name] = {};
        }

        fontMap[font_name][font_weight] = {
          base64: base64_data,
          contentUsage: content_usage,
          fontDescription: font_description,
          displayName: display_name || font_name?.charAt(0)?.toUpperCase() + font_name?.slice(1),
          sizeFactor: size_factor
        };
      });

      // Cache the loaded fonts
      this.fontCache?.set('allFonts', fontMap);
      console.log(`✅ Loaded ${fonts?.length} font variants from database`);
      console.log(`📋 Available fonts: ${Object.keys(fontMap)?.join(', ')}`);
      return fontMap;

    } catch (error) {
      console.error('❌ Fatal error loading fonts:', error);
      // Return empty object instead of failing completely
      return {};
    }
  }

  /**
   * Register fonts with jsPDF instance
   * Adds all font variants to PDF document
   * @param {jsPDF} pdf - jsPDF instance
   * @returns {Promise<boolean>} True if fonts registered successfully
   */
  async registerFonts(pdf) {
    try {
      const fonts = await this.getFonts();

      if (!fonts || Object.keys(fonts)?.length === 0) {
        console.warn('⚠️ No fonts available for registration');
        return false;
      }

      let registeredCount = 0;

      // Register each font variant with jsPDF
      Object.entries(fonts)?.forEach(([fontName, variants]) => {
        Object.entries(variants)?.forEach(([weight, fontData]) => {
          const fileName = `${fontName}-${weight}.ttf`;

          // Skip if already loaded
          if (this.loadedFonts?.has(fileName)) {
            return;
          }

          try {
            // Add font file to PDF's virtual file system
            pdf?.addFileToVFS(fileName, fontData?.base64);

            // Register the font with jsPDF
            pdf?.addFont(fileName, fontName, weight);

            console.log('Registered fonts: ',  fontName, ' ', fileName, ' ', weight );

            this.loadedFonts?.add(fileName);
            registeredCount++;
          } catch (fontError) {
            console.warn(`⚠️ Failed to register font ${fontName} (${weight}):`, fontError?.message);
          }
        });
      });

      console.log(`✅ Registered ${registeredCount} font variants with jsPDF`);
      return registeredCount > 0;

    } catch (error) {
      console.error('❌ Error registering fonts with jsPDF:', error);
      return false;
    }
  }
}

// Export singleton instance
export const fontService = new FontService();
export default fontService;